import { createStore } from "vuex";
import {
  fetchAuthenticatedAdmin,
  updateMyPreferredLanguage,
} from "@/services/backofficeClient";
import {
  getStoredToken,
  persistToken,
  clearStoredToken,
} from "@/services/session";
import { fetchBackofficeConfig } from "@/services/backofficeClient";
import { hasRole } from "@/services/acl";

let authBootstrapPromise = null;

export default createStore({
  state: {
    hideConfigButton: false,
    isPinned: false,
    showConfig: false,
    sidebarType: "bg-white",
    isRTL: false,
    mcolor: "",
    darkMode: false,
    isNavFixed: false,
    isAbsolute: false,
    showNavs: true,
    showSidenav: true,
    showNavbar: true,
    showFooter: true,
    showMain: true,
    layout: "default",
    authToken: getStoredToken(),
    admin: null,
    authLoading: false,
    authInitialized: false,
    tenantConfig: null,
  },
  mutations: {
    toggleConfigurator(state) {
      state.showConfig = !state.showConfig;
    },
    sidebarMinimize(state) {
      let sidenav_show = document.querySelector("#app");
      if (state.isPinned) {
        sidenav_show.classList.add("g-sidenav-hidden");
        sidenav_show.classList.remove("g-sidenav-pinned");
        state.isPinned = false;
      } else {
        sidenav_show.classList.add("g-sidenav-pinned");
        sidenav_show.classList.remove("g-sidenav-hidden");
        state.isPinned = true;
      }
    },
    sidebarType(state, payload) {
      state.sidebarType = payload;
    },
    navbarFixed(state) {
      if (state.isNavFixed === false) {
        state.isNavFixed = true;
      } else {
        state.isNavFixed = false;
      }
    },
    setAuthToken(state, token) {
      state.authToken = token;
    },
    setAdmin(state, admin) {
      state.admin = admin;
    },
    setAuthLoading(state, isLoading) {
      state.authLoading = isLoading;
    },
    setAuthInitialized(state, initialized) {
      state.authInitialized = initialized;
    },
    setTenantConfig(state, config) {
      state.tenantConfig = config || null;
    },
  },
  actions: {
    async fetchTenantConfig({ commit }) {
      try {
        const tenantInfo = await fetchBackofficeConfig();
        commit("setTenantConfig", tenantInfo);
      } catch (error) {
        commit("setTenantConfig", null);
        console.error("Failed to fetch backoffice config:", error);
        throw error;
      }
    },
    toggleSidebarColor({ commit }, payload) {
      commit("sidebarType", payload);
    },
    async fetchAndStoreAdmin({ commit }) {
      const admin = await fetchAuthenticatedAdmin();
      commit("setAdmin", admin);
      return admin;
    },
    async updatePreferredLanguage({ commit }, preferredLanguage) {
      const admin = await updateMyPreferredLanguage(preferredLanguage);
      commit("setAdmin", admin);
      return admin;
    },
    async initializeAuth({ state, commit, dispatch }) {
      if (state.authInitialized) {
        return state.admin;
      }

      if (!state.authToken) {
        commit("setAuthInitialized", true);
        return null;
      }

      if (authBootstrapPromise) {
        return authBootstrapPromise;
      }

      commit("setAuthLoading", true);

      authBootstrapPromise = dispatch("fetchAndStoreAdmin")
        .then((admin) => admin)
        .catch(async (error) => {
          if (error?.status === 401) {
            await dispatch("logout");
          }
          throw error;
        })
        .finally(() => {
          commit("setAuthLoading", false);
          commit("setAuthInitialized", true);
          authBootstrapPromise = null;
        });

      return authBootstrapPromise;
    },
    async establishSession({ commit, dispatch }, token) {
      commit("setAuthToken", token);
      persistToken(token);
      commit("setAuthInitialized", false);
      commit("setAuthLoading", true);

      try {
        return await dispatch("fetchAndStoreAdmin");
      } finally {
        commit("setAuthLoading", false);
        commit("setAuthInitialized", true);
      }
    },
    logout({ commit }) {
      clearStoredToken();
      commit("setAuthToken", null);
      commit("setAdmin", null);
      commit("setAuthInitialized", false);
      commit("setAuthLoading", false);
    },
  },
  getters: {
    hasJwt: (state) => Boolean(state.authToken),
    isAuthenticated: (state) => Boolean(state.authToken && state.admin),
    currentAdmin: (state) => state.admin,
    tenantAppName: (state) => state.tenantConfig?.appName,
    supportedLanguages: (state) => state.tenantConfig?.languages || [],
    preferredLanguage: (state) =>
      state.admin?.preferredLanguage ||
      state.tenantConfig?.defaultLanguage ||
      "en",
    hasRole: (state) => (role) => hasRole(state.admin, role),
  },
});
